import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import {
    advanceConsentStep,
    createConsentInstanceFromCase,
    downloadConsentDocument,
    ensureConsentForPatient,
    generateConsentCsv,
    generateConsentPdf,
    getConsentEvidenceSummary,
    getConsentInstance,
    getConsentTemplateDetail,
    isCaseConsentRequired,
    isCaseProcedureUnlocked,
    listConsentModuleInstances,
    listConsentRiskAcknowledgements,
    listConsentInstancesByCase,
    refuseConsentInstance,
    revokeConsentInstance,
    sendConsentCopyEmail,
    submitConsentSignature,
    submitConsentSignatureAndFinalizePdf,
    upsertConsentModuleInstance,
} from '@/services/surgical-consent.service';

type StepName =
    | 'patient_education'
    | 'procedure_explanation'
    | 'risks_alternatives'
    | 'patient_questions'
    | 'doctor_notes'
    | 'ready_for_signatures'
    | 'revision_safe_pdf'
    | 'emergency_override';

interface SurgicalConsentContextValue {
    isBusy: boolean;
    error: string | null;
    clearError: () => void;
    createFromCase: (input: {
        caseId: string;
        templateId: string;
        doctorId: string;
        appointmentId?: string;
        treatmentId?: string;
        actor: string;
    }) => Promise<Record<string, unknown>>;
    advanceStep: (input: {
        consentId: string;
        step: StepName;
        actor: string;
        payload?: Record<string, unknown>;
    }) => Promise<Record<string, unknown>>;
    submitSignature: (input: {
        consentId: string;
        signerRole: 'patient' | 'doctor';
        signatureType: string;
        signatureValue: string;
        actor: string;
        signatureRequestId?: string;
        signatureAuditId?: string;
        artifactId?: string;
        clientIp?: string;
        clientUserAgent?: string;
        signerConfirmedIdentity?: boolean;
    }) => Promise<Record<string, unknown>>;
    submitSignatureAndFinalizePdf: (input: {
        consentId: string;
        signerRole: 'patient' | 'doctor';
        signatureType: string;
        signatureValue: string;
        actor: string;
        signatureRequestId?: string;
        signatureAuditId?: string;
        artifactId?: string;
        clientIp?: string;
        clientUserAgent?: string;
        signerConfirmedIdentity?: boolean;
    }) => Promise<Record<string, unknown>>;
    generatePdf: (input: { consentId: string; actor?: string }) => Promise<Record<string, unknown>>;
    revoke: (input: { consentId: string; reason: string; actor: string }) => Promise<Record<string, unknown>>;
    refuse: (input: { consentId: string; reason: string; actor: string }) => Promise<Record<string, unknown>>;
    getEvidenceSummary: (consentId: string) => Promise<Record<string, unknown>>;
    getInstance: (consentId: string) => Promise<Record<string, unknown> | null>;
    listByCase: (caseId: string) => Promise<Record<string, unknown>[]>;
    listRiskAcknowledgements: (consentId: string) => Promise<Record<string, unknown>[]>;
    listModuleInstances: (consentId: string) => Promise<Record<string, unknown>[]>;
    upsertModuleInstance: (input: {
        consentId: string;
        moduleId: string;
        status: 'pending' | 'in_progress' | 'completed' | 'waived';
        moduleData?: Record<string, unknown>;
    }) => Promise<void>;
    getTemplateDetail: (templateId: string) => Promise<Record<string, unknown> | null>;
    getCaseProcedureGate: (caseId: string) => Promise<{ required: boolean; unlocked: boolean }>;
    ensureForPatient: (input: {
        patientId: string;
        practiceId: string;
        doctorId: string;
        actor: string;
    }) => Promise<Record<string, unknown>>;
    sendCopyEmail: (input: { consentId: string; recipientEmail?: string }) => Promise<Record<string, unknown>>;
    downloadDocument: (consentId: string) => Promise<string | null>;
    generateCsv: (input: { consentId: string; actor?: string }) => Promise<Record<string, unknown>>;
}

const SurgicalConsentContext = createContext<SurgicalConsentContextValue | null>(null);

export function useSurgicalConsent(): SurgicalConsentContextValue {
    const context = useContext(SurgicalConsentContext);
    if (!context) {
        throw new Error('useSurgicalConsent must be used within SurgicalConsentProvider');
    }
    return context;
}

export function SurgicalConsentProvider({ children }: { children: ReactNode }) {
    const [isBusy, setIsBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const clearError = useCallback(() => setError(null), []);

    const run = useCallback(async <T,>(operation: () => Promise<T>): Promise<T> => {
        setIsBusy(true);
        setError(null);
        try {
            return await operation();
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Unbekannter Fehler');
            throw err;
        } finally {
            setIsBusy(false);
        }
    }, []);

    const createFromCase = useCallback(
        (input: {
            caseId: string;
            templateId: string;
            doctorId: string;
            appointmentId?: string;
            treatmentId?: string;
            actor: string;
        }) => run(() => createConsentInstanceFromCase(input)),
        [run]
    );

    const advanceStepAction = useCallback(
        (input: {
            consentId: string;
            step: StepName;
            actor: string;
            payload?: Record<string, unknown>;
        }) => run(() => advanceConsentStep(input)),
        [run]
    );

    const submitSignatureAction = useCallback(
        (input: {
            consentId: string;
            signerRole: 'patient' | 'doctor';
            signatureType: string;
            signatureValue: string;
            actor: string;
            signatureRequestId?: string;
            signatureAuditId?: string;
            artifactId?: string;
        clientIp?: string;
        clientUserAgent?: string;
        signerConfirmedIdentity?: boolean;
        }) => run(() => submitConsentSignature(input)),
        [run]
    );

    const submitSignatureAndFinalizePdfAction = useCallback(
        (input: {
            consentId: string;
            signerRole: 'patient' | 'doctor';
            signatureType: string;
            signatureValue: string;
            actor: string;
            signatureRequestId?: string;
            signatureAuditId?: string;
            artifactId?: string;
        clientIp?: string;
        clientUserAgent?: string;
        signerConfirmedIdentity?: boolean;
        }) => run(() => submitConsentSignatureAndFinalizePdf(input)),
        [run]
    );

    const generatePdfAction = useCallback(
        (input: { consentId: string; actor?: string }) => run(() => generateConsentPdf(input)),
        [run]
    );

    const revokeAction = useCallback(
        (input: { consentId: string; reason: string; actor: string }) =>
            run(() => revokeConsentInstance(input)),
        [run]
    );

    const refuseAction = useCallback(
        (input: { consentId: string; reason: string; actor: string }) =>
            run(() => refuseConsentInstance(input)),
        [run]
    );

    const getEvidenceSummaryAction = useCallback(
        (consentId: string) => run(() => getConsentEvidenceSummary(consentId)),
        [run]
    );

    const getInstanceAction = useCallback(
        (consentId: string) => run(() => getConsentInstance(consentId)),
        [run]
    );

    const listByCaseAction = useCallback(
        (caseId: string) => run(() => listConsentInstancesByCase(caseId)),
        [run]
    );

    const listRiskAcknowledgementsAction = useCallback(
        (consentId: string) => run(() => listConsentRiskAcknowledgements(consentId)),
        [run]
    );

    const listModuleInstancesAction = useCallback(
        (consentId: string) => run(() => listConsentModuleInstances(consentId)),
        [run]
    );

    const upsertModuleInstanceAction = useCallback(
        (input: {
            consentId: string;
            moduleId: string;
            status: 'pending' | 'in_progress' | 'completed' | 'waived';
            moduleData?: Record<string, unknown>;
        }) => run(() => upsertConsentModuleInstance(input)),
        [run]
    );

    const getTemplateDetailAction = useCallback(
        (templateId: string) => run(() => getConsentTemplateDetail(templateId)),
        [run]
    );

    const getCaseProcedureGateAction = useCallback(
        (caseId: string) =>
            run(async () => {
                const [required, unlocked] = await Promise.all([
                    isCaseConsentRequired(caseId),
                    isCaseProcedureUnlocked(caseId),
                ]);
                return { required, unlocked };
            }),
        [run]
    );

    const sendCopyEmailAction = useCallback(
        (input: { consentId: string; recipientEmail?: string }) =>
            run(() => sendConsentCopyEmail(input)),
        [run]
    );

    const downloadDocumentAction = useCallback(
        (consentId: string) => downloadConsentDocument(consentId),
        []
    );

    const generateCsvAction = useCallback(
        (input: { consentId: string; actor?: string }) => run(() => generateConsentCsv(input)),
        [run]
    );

    const ensureForPatientAction = useCallback(
        (input: {
            patientId: string;
            practiceId: string;
            doctorId: string;
            actor: string;
        }) => run(() => ensureConsentForPatient(input)),
        [run]
    );

    const value = useMemo<SurgicalConsentContextValue>(
        () => ({
            isBusy,
            error,
            clearError,
            createFromCase,
            advanceStep: advanceStepAction,
            submitSignature: submitSignatureAction,
            submitSignatureAndFinalizePdf: submitSignatureAndFinalizePdfAction,
            generatePdf: generatePdfAction,
            revoke: revokeAction,
            refuse: refuseAction,
            getEvidenceSummary: getEvidenceSummaryAction,
            getInstance: getInstanceAction,
            listByCase: listByCaseAction,
            listRiskAcknowledgements: listRiskAcknowledgementsAction,
            listModuleInstances: listModuleInstancesAction,
            upsertModuleInstance: upsertModuleInstanceAction,
            getTemplateDetail: getTemplateDetailAction,
            getCaseProcedureGate: getCaseProcedureGateAction,
            ensureForPatient: ensureForPatientAction,
            sendCopyEmail: sendCopyEmailAction,
            downloadDocument: downloadDocumentAction,
            generateCsv: generateCsvAction,
        }),
        [
            advanceStepAction,
            clearError,
            createFromCase,
            downloadDocumentAction,
            ensureForPatientAction,
            error,
            generateCsvAction,
            generatePdfAction,
            getCaseProcedureGateAction,
            getEvidenceSummaryAction,
            getInstanceAction,
            getTemplateDetailAction,
            isBusy,
            listByCaseAction,
            listModuleInstancesAction,
            listRiskAcknowledgementsAction,
            refuseAction,
            revokeAction,
            sendCopyEmailAction,
            submitSignatureAction,
            submitSignatureAndFinalizePdfAction,
            upsertModuleInstanceAction,
        ]
    );

    return (
        <SurgicalConsentContext.Provider value={value}>{children}</SurgicalConsentContext.Provider>
    );
}
